import { getDehydratedState, prefetchMotionGraphicsSearch } from '@/utils/search/ssr';
import { HydrationBoundary, QueryClient, type DehydratedState } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryState } from '@/hooks/state/useQueryState';
import { useListingInfiniteScrollActive } from '@/hooks/useListingInfiniteScrollActive';
import { useShowMoreVariant } from '@/hooks/useShowMoreVariant';
import { MGTile } from '@/components/MGTile';
import { SEO } from '@/components/SEO';
import TypesenseFilters, { useTypesenseFilters } from '@/components/TypesenseFilters';
import { ASSET_TYPES } from '@/components/TypesenseFilters/assetTypes';
import { Header, HeaderTitle, HeaderDescription, HeaderBreadcrumbs, HeaderTicks } from '@/components/ui/header';
import { setAuthAwareCacheHeaders } from '@/utils/pages/cache-headers';
import { useTypesenseSearch } from '@/hooks/useTypesenseSearch';
import { MOTION_GRAPHICS_FILTER, getSearches, isValidSort, type SortOption } from '@music-vine/setlist';
import { withServerAuth } from '@/lib/auth/withServerAuth';
import { getCategoryPageTitle, getFirstString } from '@/lib/utils';
import { MGProvider } from '@/providers/MGProvider';
import { setlist } from '@/utils/typesense';
import { replaceTemplateVariables } from '@/utils/template-variables';
import { convertFiltersToFilterBy } from '@/components/TypesenseFilters/filterParsing';
import type { MGCategoryItem, MotionGraphics } from '@music-vine/setlist';
import type { NextPage } from 'next';
import { useShowFreeFirst } from '@/hooks/search/shared/useShowFreeFirst';
import { useShowFreeFirstAvailable } from '@/hooks/search/shared/useShowFreeFirstAvailable';
import { FreeFirstMotionGraphicsSections } from '@/components/FreeFirstMotionGraphicsSections';
import { getSectionCopy } from '@/utils/show-free-first/sectionCopy';
import { ASSET_TYPE } from '@/types/asset';

/**
 * Motion Graphics category page - uses INFINITE SCROLLING pagination
 *
 * This page loads content dynamically using TanStack Query's infinite queries.
 * Unlike index pages that use traditional URL-based pagination (?page=2),
 * category pages append new content to the existing list when "Load More" is clicked.
 */
const MotionGraphicsSearchResults = ({
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  hasInvalidSubFilterSelection,
  isLoggedIn
}: {
  data: any;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  hasInvalidSubFilterSelection: boolean;
  isLoggedIn: boolean;
}) => {
  const showMoreVariant = useShowMoreVariant();
  const [isInfiniteStreamActive, setIsInfiniteStreamActive] = useState(false);

  // Group B: hide the site footer while the listing streams more results
  // and reveal it at the true end. Group A keeps the footer as today.
  useListingInfiniteScrollActive(showMoreVariant === 'B' && isInfiniteStreamActive);
  return <MGProvider
  // No showMoreHref: category pagination paths 404 by design
  fetchNextPage={fetchNextPage} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} noResults={hasInvalidSubFilterSelection || data?.results?.length === 0 && !isFetchingNextPage} onInfiniteStreamChange={setIsInfiniteStreamActive} showComprehensiveListings={!isLoggedIn} data-sentry-element="MGProvider" data-sentry-component="MotionGraphicsSearchResults" data-sentry-source-file="index.page.tsx">
      {data?.results?.map((hit: {
      document?: MotionGraphics;
    }) => {
      if (!(hit && 'document' in hit)) {
        return null;
      }
      const mg = hit.document;
      if (!mg) {
        return null;
      }
      return <MGTile compatibility={mg.compatibility} fileSize={mg.file_size} id={mg.id} image={mg.image} key={`mg-tile-${mg.id}`} meta={{
        contributor: mg.contributor.name,
        setLiveUnixTimestamp: mg.set_live ?? 0,
        resolution: mg.resolution,
        isPremium: mg.is_premium,
        compatibility: mg.compatibility,
        onAlpha: mg.on_alpha,
        relevanceFree: mg.relevance_free,
        relevancePremium: mg.relevance_premium,
        isFeatured: mg.is_featured,
        downloadCountFree: mg.free_download_count,
        playCountFree: mg.free_play_count,
        downloadCountPremium: mg.premium_download_count,
        playCountPremium: mg.premium_play_count
      }} mgSlug={mg.slug} title={mg.name} video={mg.preview_video_url} />;
    })}
    </MGProvider>;
};

// Wrapper component to access TypesenseFilters context for search logic
const MotionGraphicsPageContent = ({
  shouldIntersperseFreeAndPremium,
  isLoggedIn,
  currentSort,
  onHasNextPageChange
}: {
  shouldIntersperseFreeAndPremium: boolean;
  isLoggedIn: boolean;
  currentSort: SortOption;
  onHasNextPageChange: (hasNextPage: boolean) => void;
}) => {
  const [showFreeFirst] = useShowFreeFirst();
  const freeFirstAvailable = useShowFreeFirstAvailable(ASSET_TYPES.MOTION_GRAPHICS);
  const freeFirstActive = showFreeFirst && freeFirstAvailable;
  // Get filter data from the TypesenseFilters context
  const {
    selectedFilters,
    secondaryToggles,
    getCombinedFilterString,
    urlSearchTerms
  } = useTypesenseFilters();

  // Get combined filters from context (includes preselected + user filters)
  const combinedFilters = getCombinedFilterString();

  // Apply the MOTION_GRAPHICS_FILTER in addition to other filters
  const filter = combinedFilters ? `${combinedFilters} && ${MOTION_GRAPHICS_FILTER}` : MOTION_GRAPHICS_FILTER;

  // Handle search terms array conversion
  const searchTermsArray = Array.isArray(urlSearchTerms) ? urlSearchTerms : urlSearchTerms ? [urlSearchTerms] : [];

  // Get search terms from URL
  const searchQuery = searchTermsArray.length > 0 ? searchTermsArray.join(', ') : undefined;

  // Generate searches
  const searches = getSearches({
    q: searchQuery,
    filter_by: filter,
    sort: currentSort,
    fetchFreeAndPremium: shouldIntersperseFreeAndPremium,
    isHomePage: false
  });
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useTypesenseSearch<MotionGraphics>({
    searches,
    cacheOptions: {
      cacheSearchResultsForSeconds: 60
    },
    shouldIntersperseFreeAndPremium,
    keepPrevious: true // Show previous results while new ones load
  });

  // Check for invalid sub-filter selection
  // This happens when a primary compatibility is selected but all secondary toggles are unchecked
  // Exception: "Drag & Drop" (compatibility ID 5) doesn't have secondary filters, so it's always valid
  // Exception: Preselected compatibility filters are always valid (category pages)
  const hasInvalidSubFilterSelection = useMemo(() => {
    const compatibilityFilter = selectedFilters['compatibility.id'];
    const hasPrimaryCompatibility = compatibilityFilter?.included?.length > 0;
    if (!hasPrimaryCompatibility) {
      return false;
    }

    // 🎯 If secondary toggles are hidden (preselected category), selection is always valid
    if (!secondaryToggles.show) {
      return false;
    }

    // Check if the only selected compatibility is "Drag & Drop" (ID 5)
    const compatibilityIds = compatibilityFilter.included || [];
    const isDragAndDropOnly = compatibilityIds.length === 1 && compatibilityIds[0] === '5'; // Drag & Drop ID

    if (isDragAndDropOnly) {
      return false; // "Drag & Drop" doesn't have secondary filters, so it's always valid
    }

    // Check if any secondary toggles are enabled
    const hasEnabledSecondaryToggle = secondaryToggles.values.some((toggle: any) => toggle.value === true);
    return !hasEnabledSecondaryToggle;
  }, [selectedFilters, secondaryToggles]);

  // Notify parent component about hasNextPage changes for SEO links
  useEffect(() => {
    onHasNextPageChange(hasNextPage);
  }, [hasNextPage, onHasNextPageChange]);
  return <>
      {freeFirstActive ? <FreeFirstMotionGraphicsSections assetType={ASSET_TYPE.MOTION} filter={filter} premiumHeading={getSectionCopy({
      assetType: ASSET_TYPE.MOTION,
      pageType: 'category'
    }).premiumHeading} q={urlSearchTerms ? urlSearchTerms.join(' ') : undefined} sort={currentSort} /> : <MotionGraphicsSearchResults data={data} fetchNextPage={fetchNextPage} hasInvalidSubFilterSelection={hasInvalidSubFilterSelection} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} isLoggedIn={isLoggedIn} />}
    </>;
};
export interface MotionGraphicsPageProps {
  categoryItem: MGCategoryItem;
  dehydratedState?: DehydratedState;
  isLoggedIn: boolean;
  shouldIntersperseFreeAndPremium: boolean;
  totalCount?: number;
}
const Page: NextPage<MotionGraphicsPageProps> = ({
  categoryItem,
  shouldIntersperseFreeAndPremium,
  isLoggedIn,
  dehydratedState,
  totalCount
}) => {
  const router = useRouter();
  const [hasNextPage, setHasNextPage] = useState(false);
  const {
    id,
    meta_title,
    primary_heading,
    primary_heading_visitor,
    description,
    meta_description,
    name,
    slug,
    relative_url,
    breadcrumbs,
    breadcrumb_children
  } = categoryItem;

  // Base URL for canonical and pagination links
  const siteUrl = (process.env.NEXT_PUBLIC_SITE_URL ?? 'https://uppbeat.io').replace(/\/$/, '');
  const categoryPath = relative_url ?? `/motion-graphics/${slug}`;

  // Create preselected filters for Motion Graphics (excluding LUTs)
  // Don't add is_lut filter here since it's already in MOTION_GRAPHICS_FILTER
  const preSelectedFilters = useMemo(() => setlist.getCategoryPreselectedFilters(categoryItem), [categoryItem]);

  // Use useQueryState to read sort parameter consistently with TypesenseSort components
  const [currentSort] = useQueryState('sort', {
    defaultValue: 'relevancy' as SortOption,
    parse: value => isValidSort(value) ? value : 'relevancy',
    serialize: value => value
  });

  // Callback to update hasNextPage from child component
  const handleHasNextPageChange = useCallback((nextPageExists: boolean) => {
    setHasNextPage(nextPageExists);
  }, []);
  const pageTitle = getCategoryPageTitle({
    primary_heading_visitor,
    primary_heading,
    name,
    userIsAuthenticated: isLoggedIn
  });
  const pageDescription = replaceTemplateVariables(getFirstString(description), {
    count: totalCount
  });
  const seoTitle = getFirstString(meta_title, `${pageTitle} • Motion Graphics from Uppbeat`);
  const seoDescription = replaceTemplateVariables(getFirstString(meta_description, 'Transform your content with trending animations, video templates and overlays from the motion graphics library for creators. Sign up and download for free!'), {
    count: totalCount
  });
  return <HydrationBoundary state={dehydratedState} data-sentry-element="HydrationBoundary" data-sentry-component="Page" data-sentry-source-file="index.page.tsx">
      <div>
        <SEO canonical={`${siteUrl}${categoryPath}`} description={seoDescription} title={seoTitle} data-sentry-element="SEO" data-sentry-source-file="index.page.tsx" />

        <Header separator={true} ticks={!isLoggedIn} data-sentry-element="Header" data-sentry-source-file="index.page.tsx">
          <HeaderBreadcrumbs assetType="motion" current={{
          id,
          label: name,
          slug,
          relative_url: relative_url as string
        }} next={breadcrumb_children} previous={breadcrumbs} data-sentry-element="HeaderBreadcrumbs" data-sentry-source-file="index.page.tsx" />

          <HeaderTitle data-sentry-element="HeaderTitle" data-sentry-source-file="index.page.tsx">{pageTitle}</HeaderTitle>

          {pageDescription && <HeaderDescription>{pageDescription}</HeaderDescription>}

          {!isLoggedIn && <HeaderTicks content={['Motion Graphics for creators', 'No copyright issues', 'Download now']} />}
        </Header>
        <TypesenseFilters assetType={ASSET_TYPES.MOTION_GRAPHICS} facets={[{
        value: 'types.id',
        label: 'Type',
        display: 'popover'
      }, {
        value: 'styles.id',
        label: 'Style',
        display: 'popover'
      }, {
        value: 'compatibility.id',
        label: 'Compatibility',
        display: 'toggle',
        separator: true
      }]} isSticky={true} preSelectedFilters={preSelectedFilters} data-sentry-element="TypesenseFilters" data-sentry-source-file="index.page.tsx">
          <MotionGraphicsPageContent currentSort={currentSort} isLoggedIn={isLoggedIn} onHasNextPageChange={handleHasNextPageChange} shouldIntersperseFreeAndPremium={shouldIntersperseFreeAndPremium} data-sentry-element="MotionGraphicsPageContent" data-sentry-source-file="index.page.tsx" />
        </TypesenseFilters>
      </div>
    </HydrationBoundary>;
};
type MgCategoryPageParams = {
  mgCategoryItemSlug: string;
  mgCategoryPath: string[];
};
export const getServerSideProps = withServerAuth<MotionGraphicsPageProps>(async (auth, context) => {
  const isLoggedIn = auth.isAuthenticated;
  // If not authenticated OR has 'Free' role, show interspersed results
  const shouldIntersperseFreeAndPremium = !auth.isAuthenticated || auth.hasRole('Free');
  const {
    params,
    req,
    res
  } = context;
  const authState = req.headers['x-auth-state'];
  if (authState) {
    res.setHeader('x-auth-state', authState);
  }
  const {
    mgCategoryPath
  } = params as MgCategoryPageParams;

  // Category pages use infinite scroll only - pagination paths are not supported
  const pathArray = Array.isArray(mgCategoryPath) ? mgCategoryPath : [mgCategoryPath];

  // Return 404 for category pagination paths (e.g., /types/intros/page/2)
  const isPaginationPath = pathArray.length >= 2 && pathArray[pathArray.length - 2] === 'page';
  if (isPaginationPath) {
    res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate');
    return {
      notFound: true
    };
  }
  const slugString = `/motion-graphics/${pathArray.join('/')}`;
  if (!slugString) {
    res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate');
    return {
      notFound: true
    };
  }
  const [style, pageRedirect] = await Promise.all([setlist.getMotionGraphicsCategory(slugString), setlist.getPageRedirect(slugString)]);
  if (pageRedirect) {
    return {
      redirect: {
        destination: pageRedirect.destination,
        permanent: pageRedirect.permanent
      }
    };
  }
  const mgSlugIsValid = style !== null;
  if (!mgSlugIsValid) {
    res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate');
    return {
      notFound: true
    };
  }

  // Cache the page for 1 day, but allow stale while revalidating.
  // Only set on successful responses to avoid caching 404s/errors, and only
  // for logged-out visitors — every logged-in one shares the `?_auth=1`
  // cache key, so a `public` response there would leak the first
  // subscriber's page to the rest.
  setAuthAwareCacheHeaders(context, {
    isAuthenticated: auth.isAuthenticated,
    cacheTagPath: slugString
  });
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Number.POSITIVE_INFINITY
      }
    }
  });

  // Serialize the preselected filters through the same parse + converter
  // the client's first render uses, so the prefetched query key is
  // byte-identical. The old hand-rolled serializer emitted
  // `tags.id:=[671]` where the client asks for `tags.id:671`, so the
  // dehydrated data was never read and the grid SSR'd as skeletons.
  const preselectedFilterBy = convertFiltersToFilterBy(setlist.getCategoryPreselectedFilters(style), ASSET_TYPES.MOTION_GRAPHICS);

  // Add motion graphics filter to match what client will use
  const combinedFilter = preselectedFilterBy ? `${preselectedFilterBy} && ${MOTION_GRAPHICS_FILTER}` : MOTION_GRAPHICS_FILTER;
  let totalCount: number | undefined;
  try {
    // Prefetch search results for hydration with proper filters
    const prefetchResult = await prefetchMotionGraphicsSearch({
      queryClient,
      categorySlug: pathArray.join('/'),
      preselectedFilters: combinedFilter,
      fetchFreeAndPremium: shouldIntersperseFreeAndPremium,
      isHomePage: false,
      // The client reads `sort` straight off the URL, so the prefetch has to
      // as well — otherwise a sorted URL prefetches under the relevancy key
      // the grid never asks for, and pays for the search twice (DEV-6490).
      initialSort: isValidSort(context.query.sort) ? context.query.sort : 'relevancy'
    });
    totalCount = prefetchResult?.totalCount;
  } catch {
    // Continue without prefetch
  }
  return {
    props: {
      categoryItem: style,
      shouldIntersperseFreeAndPremium,
      isLoggedIn,
      dehydratedState: getDehydratedState(queryClient),
      totalCount
    }
  };
});
export default Page;